import { Capacitor } from '@capacitor/core';

import { RoisFileSaver } from '../plugins/roisFileSaver';
import { showErrorToast, showFileSavedToast } from './toasts';
import i18n from '../i18n';

/**
 * Punto ÚNICO por el que sale un fichero de la aplicación.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 *
 * Toda la app descargaba igual: un `Blob`, `URL.createObjectURL()` y un
 * `<a download>` invisible. En el navegador funciona; dentro del APK **no hace
 * nada**. El WebView de Android no interpreta las URL `blob:`, así que la
 * petición de descarga ni se forma y el `DownloadListener` no se dispara nunca
 * (limitación conocida de Capacitor: ionic-team/capacitor#5478, abierta desde
 * 2022 y sin arreglo oficial).
 *
 * ── Por qué UN punto y no diez parches ─────────────────────────────────────
 *
 * Ese patrón estaba copiado siete veces, y los tres Excel ni siquiera pasaban
 * por él (`XLSX.writeFile` se guisa la descarga por dentro). Con diez copias,
 * la próxima descarga que alguien añada volvería a nacer rota — y como el fallo
 * es SILENCIOSO, nadie se enteraría hasta que un operario lo dijera.
 *
 * ── Qué NO cambia ──────────────────────────────────────────────────────────
 *
 * El camino web es exactamente el de antes, línea por línea. Es el que usan a
 * diario decenas de personas y el que cubren los E2E; este cambio existe para
 * arreglar la app, no para tocar la web.
 */

/**
 * Cuánto contenido viaja en cada mensaje al puente.
 *
 * 🔴 El puente de Capacitor tiene un techo y **rebasarlo no da un error: cuelga
 * la aplicación**. `postToNative` hace
 * `androidBridge.postMessage(JSON.stringify(…))` dentro de un `try/catch` que
 * solo hace `console.error`, así que cuando Java lanza —y lanza si el mensaje
 * es enorme— la promesa del plugin no se resuelve NI se rechaza. Medido en
 * tablet (2026-08-16) con un ZIP de 211 fotos: indicador girando para siempre.
 *
 * 512 KB por trozo (~700 KB ya en base64) queda muy por debajo de cualquier
 * límite razonable. Se prefiere ir sobrado antes que apurar: el precio de
 * quedarse corto son unos milisegundos más, y el de pasarse es un cuelgue.
 */
export const CHUNK_SIZE = 512 * 1024;

/**
 * Cuánto se espera a que el lado nativo conteste antes de darlo por perdido.
 *
 * Es la RED contra el cuelgue de arriba. Aunque los trozos van sobrados de
 * tamaño, un mensaje tragado por el puente dejaría la promesa colgada para
 * siempre, y eso —una pantalla congelada sin explicación— es justo el fallo que
 * este módulo existe para erradicar. Con el reloj, lo peor que puede pasar es
 * un error visible.
 *
 * Generoso a propósito: escribir un trozo es cosa de milisegundos, así que solo
 * salta si algo va de verdad mal.
 */
const BRIDGE_TIMEOUT_MS = 30_000;

/** Código estable del reloj, para distinguirlo de un fallo del propio guardado. */
export const BRIDGE_TIMEOUT_CODE = 'FILE_SAVE_BRIDGE_TIMEOUT';

/**
 * Contenido en base64 SIN el prefijo `data:...;base64,`.
 *
 * El puente de Capacitor mueve JSON, no binario: un `Blob` no cruza. Hay que
 * convertirlo, y el prefijo que añade `FileReader` tiene que caer aquí — si se
 * colara, el decodificador nativo escribiría basura en el fichero.
 */
const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });

/**
 * Envuelve una llamada al puente con un reloj.
 *
 * Sin esto, un mensaje que el puente se trague deja la interfaz colgada sin
 * decir nada. Un fallo ruidoso siempre es preferible a un silencio.
 */
const withTimeout = async <T>(promise: Promise<T>, what: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          const error = new Error(
            `El puente nativo no respondió a "${what}" en ${BRIDGE_TIMEOUT_MS} ms`,
          );
          (error as Error & { code?: string }).code = BRIDGE_TIMEOUT_CODE;
          reject(error);
        }, BRIDGE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Abre un fichero ya guardado.
 *
 * NUNCA lanza: para cuando se llama, el fichero ya está en Descargas. Que la
 * tablet no tenga con qué abrir un PDF —caso real en una tablet pelada— no
 * puede presentarse al operario como si la descarga hubiera fallado.
 */
export async function openSavedFile(uri: string, mimeType: string): Promise<void> {
  try {
    await RoisFileSaver.open({ uri, mimeType });
  } catch (error) {
    console.warn('[descargas] no se pudo abrir el fichero', error);
    showErrorToast(i18n.t('toasts:error.generic'));
  }
}

/**
 * Escribe el contenido a trozos y devuelve la URI del fichero.
 *
 * Va SIEMPRE a trozos, también para un PDF de 90 KB que cabría en un solo
 * mensaje: un camino que solo se recorre con ficheros grandes es un camino que
 * nadie prueba, y es exactamente así como el cuelgue del ZIP llegó a la tablet.
 */
const writeInChunks = async (blob: Blob, filename: string, mimeType: string): Promise<string> => {
  const { token } = await withTimeout(
    RoisFileSaver.beginWrite({ filename, mimeType }),
    'beginWrite',
  );

  try {
    for (let start = 0; start < blob.size; start += CHUNK_SIZE) {
      const chunk = blob.slice(start, Math.min(start + CHUNK_SIZE, blob.size));
      await withTimeout(
        RoisFileSaver.writeChunk({ token, base64: await toBase64(chunk) }),
        'writeChunk',
      );
    }
  } catch (error) {
    // Deja de existir el fichero a medias. No se espera ni se comprueba: es
    // limpieza, y no puede tapar el error de verdad que estamos relanzando.
    void RoisFileSaver.abortWrite({ token }).catch(() => undefined);
    throw error;
  }

  // Un blob vacío es un fichero vacío legítimo: `beginWrite` + `endWrite` sin
  // ningún trozo por el medio lo produce sin ningún caso especial.
  const { uri } = await withTimeout(RoisFileSaver.endWrite({ token }), 'endWrite');
  return uri;
};

/**
 * Entrega un fichero al usuario.
 *
 * En el navegador lo descarga como siempre. En la app lo guarda en Descargas y
 * ofrece abrirlo.
 *
 * @param blob contenido; su `type` viaja como MIME y decide qué apps ofrece
 *   Android al abrirlo.
 * @param filename nombre CON extensión.
 */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }

  // Un MIME vacío dejaría a Android sin saber qué app ofrecer.
  const mimeType = blob.type || 'application/octet-stream';

  let uri: string;
  try {
    uri = await writeInChunks(blob, filename, mimeType);
  } catch (error) {
    // Aquí NO hay fichero. Tragárselo sería volver justo al fallo silencioso
    // que este módulo viene a arreglar, así que se avisa Y se relanza para que
    // quien pidió la descarga se entere.
    showErrorToast(i18n.t('toasts:error.saveFailed'));
    throw error;
  }

  showFileSavedToast(filename, () => {
    void openSavedFile(uri, mimeType);
  });
}

export default saveBlob;
